import API from './api'

const {REACT_APP_API_URI} = process.env;

const updateProperty = (subject, keyProperty, valueProperty, collection, defaultValue) => {
    if(typeof defaultValue == "undefined") {
        defaultValue = null;
    }
    const key = subject[keyProperty];
    if(key && collection[key]) {
        subject[valueProperty] = collection[key]
    } else {
        subject[valueProperty] = defaultValue;
    }
    return subject
}

const formatNetworks = (response) => {
    // Add null checks for response data
    if (!response || !response.data || !response.data.data) {
        return [];
    }

    const data = response.data.data;
    const networks = data.networks ? Object.values(data.networks) : [];
    const users = data.users || {};
    const images = data.images || {};
    const locations = data.locations ? Object.values(data.locations) : [];
  
    try {
        return networks.map((network) => {
            network = updateProperty(network, "created_by", "created_by", users);
            network = updateProperty(network, "updated_by", "updated_by", users);
            network = updateProperty(network, "deleted_by", "deleted_by", users);
            
            network = updateProperty(network, "image_id", "image", images);
            let location = locations.find((item) => item.id === network.location_id)
            network.city = location && location.city ? location.city : '';
            network.state = location && location.state ? location.state : '';
            network.country = location &&  location.country ? location.country : '';
            
            // Convert date fields to strings to prevent JSON serialization errors
            if (network.created_at) {
                network.created_at = network.created_at instanceof Date ? network.created_at.toISOString() : network.created_at;
            }
            if (network.updated_at) {
                network.updated_at = network.updated_at instanceof Date ? network.updated_at.toISOString() : network.updated_at;
            }
            if (network.deleted_at) {
                network.deleted_at = network.deleted_at instanceof Date ? network.deleted_at.toISOString() : network.deleted_at;
            }
            if (network.billing_date) {
                network.billing_date = network.billing_date instanceof Date ? network.billing_date.toISOString() : network.billing_date;
            }
            
            return network;
        });
    } catch (error) {
        console.error('Error formatting networks:', error);
        return [];
    }
}

const formatNetwork = (response) => {
    // Add null checks for response data
    if (!response || !response.data || !response.data.data) {
        return {};
    }

    const data = response.data.data;
    let network = data.network || {};
    const users = data.users || {};
    const images = data.images || {};

    network = updateProperty(network, "created_by", "created_by", users);
    network = updateProperty(network, "updated_by", "updated_by", users);
    network = updateProperty(network, "deleted_by", "deleted_by", users);
    network = updateProperty(network, "image_id", "image", images);
    
    // Convert date fields to strings to prevent JSON serialization errors
    if (network.created_at) {
        network.created_at = network.created_at instanceof Date ? network.created_at.toISOString() : network.created_at;
    }
    if (network.updated_at) {
        network.updated_at = network.updated_at instanceof Date ? network.updated_at.toISOString() : network.updated_at;
    }
    if (network.deleted_at) {
        network.deleted_at = network.deleted_at instanceof Date ? network.deleted_at.toISOString() : network.deleted_at;
    }
    if (network.billing_date) {
        network.billing_date = network.billing_date instanceof Date ? network.billing_date.toISOString() : network.billing_date;
    }
    
    return network
}

class NetworkApi {
    constructor(token) {
        this.api = new API(token);
        this.baseUrl = `${REACT_APP_API_URI}/admin/network`
    }

    all(status, pageNumber) {
        try {
            const page = pageNumber || 1;
            return this.api.get(`${this.baseUrl}?status=${status}&page_num=${page}`)
            .then((response) => {
                return {
                    networks: formatNetworks(response),
                    total_pages: response.data?.data?.total_pages || 0,
                }
            })
            .catch((error) => {
                console.error('Error fetching networks:', error);
                return {
                    networks: [],
                    total_pages: 0,
                }
            });
        }catch(error) {
            console.error('Error in all method:', error);
            return {
                networks: [],
                total_pages: 0,
            }
        }
    }

    search(search, status, pageNumber) {
        try {
            const page = pageNumber || 1;
            return this.api.get(`${this.baseUrl}?name=${search}&status=${status}&page_num=${page}`)
            .then((response) => {
                return {
                    networks: formatNetworks(response),
                    total_pages: response.data?.data?.total_pages || 0,
                }
            })
            .catch((error) => {
                console.error('Error searching networks:', error);
                return {
                    networks: [],
                    total_pages: 0,
                }
            });
        }catch(error) {
            console.error('Error in search method:', error);
            return {
                networks: [],
                total_pages: 0,
            }
        }
    }

    getById(id) {
        try {
            return this.api.get(`${this.baseUrl}/${id}`)
            .then((response) => {
                return formatNetwork(response);
            });
        } catch(error) {
            console.error(`Error fetching network. id=${id}, error=${error}`);
            return {}
        }
    }

    update(id, body) {
        // Check if body is FormData and send as multipart, otherwise send as JSON
        if (body instanceof FormData) {
            return this.api.patch(`${this.baseUrl}/${id}`, body, "multipart/form-data")
            .then((response) => {
                return formatNetwork(response);
            })
            .catch((error) => {
                console.error(`Error updating network. id=${id}, error=${error}`);
                if (error.response) {
                    console.error('Error response data:', error.response.data);
                    console.error('Error response status:', error.response.status);
                }
                throw error; // Re-throw to allow proper error handling
            });
        } else {
            // Convert object to JSON for non-FormData updates
            const jsonData = {};
            Object.assign(jsonData, body);
            
            return this.api.patch(`${this.baseUrl}/${id}`, jsonData)
            .then((response) => {
                return formatNetwork(response);
            })
            .catch((error) => {
                console.error(`Error updating network. id=${id}, error=${error}`);
                throw error; // Re-throw to allow proper error handling
            });
        }
    }

    create(body) {
        return this.api.post(this.baseUrl, body, "multipart/form-data")
        .then((response) => {
            // Return the raw response data for create, as it may have different structure
            return response.data?.data || response.data || response;
        })
        .catch((error) => {
            console.error(`Error creating network. error=${error}`);
            if (error.response) {
                console.error('Error response data:', error.response.data);
                console.error('Error response status:', error.response.status);
            }
            throw error; // Re-throw to allow proper error handling
        });
    }

    delete(id) {
        try{
            return this.api.delete(`${this.baseUrl}/${id}`)
            .then((response) => {
                return response.data.data;
            });
        }catch(error) {
            console.error(`Error deleting network. error=${error}`);
            return {}
        }
    }

    searchByCity(param, status, pageNumber) {
        try {
            const page = pageNumber || 1;
            return this.api.get(`${this.baseUrl}?city=${param}&status=${status}&page_num=${page}`)
            .then((response) => {
                return  {
                    networks: formatNetworks(response),
                    total_pages: response.data.data.total_pages
                }
            });
        }catch(error) {
            console.error(error);
            return []
        }
    }    

    filterByLetter(status, value, pageNumber){
        try {
            let letter = value.toLowerCase()
            const page = pageNumber || 1;
            return this.api.get(`${this.baseUrl}?status=${status}&name_start=${letter}&page_num=${page}`)
                .then((response) => {
                    return {
                        networks: formatNetworks(response),
                        total_pages: response.data.data.total_pages
                    }
                });
        } catch (error) {
            console.error(error);
            return []
        }
    }

    getListByLetter(status, value){
        try {
            let letter = value.toLowerCase()
            return this.api.get(`${this.baseUrl}?status=${status}&name_start=${letter}&max_networks=${10}`)
                .then((response) => {
                    return {
                        networks: formatNetworks(response),
                        total_pages: response.data.data.total_pages
                    }
                });
        } catch (error) {
            console.error(error);
            return []
        }
    }
}

export default NetworkApi;
